import type {
  AccountUsageDTO, CreateWeddingBody, InvitePreviewDTO, NotificationPrefsDTO, Plan, PlannerDTO, SaveTemplate, SessionDTO, SignInMethodsDTO, TemplateDTO,
  UpdateNotificationPrefs, WeddingDTO, WeddingSummaryDTO,
} from "@bridewell/shared";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { http } from "./client";
import { keys, type ResourceModule } from "./keys";
import { useInvalidateWedding } from "./modules";

/* The signed-in person: profile, their weddings, plan usage, devices, invites they open, Pro planner + templates, reminder mail. */

export interface MeDTO { id: string; name: string; email: string; emailVerified: boolean; image: string | null; plan: Plan }

export const meQuery = queryOptions({ queryKey: keys.me, queryFn: async () => (await http.get<MeDTO>("/me")).data });
export const myWeddingsQuery = queryOptions({ queryKey: keys.myWeddings, queryFn: async () => (await http.get<WeddingSummaryDTO[]>("/me/weddings")).data });
/** What the account holds against its plan ceilings (weddings, storage, templates). */
export const usageQuery = queryOptions({ queryKey: keys.usage, queryFn: async () => (await http.get<AccountUsageDTO>("/me/usage")).data, staleTime: 15_000 });

/** The account's display name. (The name inside one wedding is `usePeople().rename`.) */
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: { name: string }) => { await http.patch("/me", patch); },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

/**
 * Which sign-in methods the server offers right now (Apple and Google switch on with their credentials). Public — the
 * sign-in screens ask before anyone is signed in — and slow to change. Linking, listing and removing an account's own
 * methods stay with the Better Auth client (`linkSocial`, `listAccounts`, `unlinkAccount`).
 */
export const signInMethodsQuery = queryOptions({
  queryKey: keys.signInMethods,
  queryFn: async () => (await http.get<SignInMethodsDTO>("/sign-in-methods")).data,
  staleTime: 60 * 60_000,
});

/**
 * The account's signed-in devices, this one first (`current`). Ids only, never a session token. Signing out here and
 * ending every other device stay with the Better Auth client (`signOut`, `revokeOtherSessions`).
 */
export const sessionsQuery = queryOptions({ queryKey: keys.sessions, queryFn: async () => (await http.get<SessionDTO[]>("/me/sessions")).data, staleTime: 15_000 });

/** Ends one other device's session (that device is signed out at its next request; this one answers 409). */
export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => { await http.del(`/me/sessions/${encodeURIComponent(sessionId)}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sessions }),
  });
}

/** A new wedding owned by this person (the server applies the plan's active-wedding ceiling and seeds the defaults). */
export function useCreateWedding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateWeddingBody) => (await http.post<WeddingDTO>("/weddings", input)).data,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.myWeddings });
      await qc.invalidateQueries({ queryKey: keys.usage });
    },
  });
}

/** An invite link's public preview (couple, date, role). The token is a bearer credential: never cached beyond the screen. */
export const invitePreviewQuery = (token: string) =>
  queryOptions({ queryKey: keys.invite(token), queryFn: async () => (await http.get<InvitePreviewDTO>(`/invites/${token}`)).data, retry: false, gcTime: 0 });

/** Link = access: accepting seeds the role's preset grants with no approval step. Answers the wedding to open. */
export function useAcceptInvite(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (displayName: string) => (await http.post<{ weddingId: string }>(`/invites/${token}/accept`, { displayName })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.myWeddings }),
  });
}

/* ───────── Pro: planner view + templates ───────── */

/** Every active wedding + the next 30 days. 403 PLAN_REQUIRED below Pro — the page shows the upsell instead. */
export const plannerQuery = queryOptions({ queryKey: keys.planner, queryFn: async () => (await http.get<PlannerDTO>("/me/planner")).data, staleTime: 15_000, retry: false });
export const templatesQuery = queryOptions({ queryKey: keys.templates, queryFn: async () => (await http.get<TemplateDTO[]>("/me/templates")).data, retry: false });

export interface ApplyResult { data: unknown[]; meta: { requested: number; created: number; skipped: number; limit: number | null } }

export function useSaveTemplate(weddingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveTemplate) => (await http.post<TemplateDTO>(`/weddings/${weddingId}/templates`, input)).data,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: keys.templates }); void qc.invalidateQueries({ queryKey: keys.usage }); },
  });
}
export function useApplyTemplate(weddingId: string) {
  const invalidate = useInvalidateWedding(weddingId);
  return useMutation({
    mutationFn: async ({ templateId, module }: { templateId: string; module: ResourceModule }) => {
      const res = await http.post<unknown[]>(`/weddings/${weddingId}/templates/${templateId}/apply`);
      return { module, data: res.data, meta: res.meta as ApplyResult["meta"] };
    },
    onSuccess: (r) => invalidate(r.module),
  });
}
export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await http.del(`/me/templates/${id}`); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: keys.templates }); void qc.invalidateQueries({ queryKey: keys.usage }); },
  });
}

/* ───────── reminder mail, digest, calendar feed ───────── */

export const notificationPrefsQuery = queryOptions({ queryKey: keys.notifications, queryFn: async () => (await http.get<NotificationPrefsDTO>("/me/notifications")).data });

export function useNotificationPrefs() {
  const qc = useQueryClient();
  const set = (p: NotificationPrefsDTO) => qc.setQueryData(keys.notifications, p);
  const update = useMutation({ mutationFn: async (patch: UpdateNotificationPrefs) => (await http.patch<NotificationPrefsDTO>("/me/notifications", patch)).data, onSuccess: set });
  const rotate = useMutation({ mutationFn: async () => (await http.post<NotificationPrefsDTO>("/me/notifications/calendar/rotate")).data, onSuccess: set });
  const stop = useMutation({ mutationFn: async () => (await http.del<NotificationPrefsDTO>("/me/notifications/calendar")).data, onSuccess: set });
  return { update, rotate, stop };
}
